import axios from 'axios';
import { getApiBaseUrl } from '../config/apiBase';

// PROMPT-133: Host-Based API Calls (No localhost)
const API_BASE_URL = getApiBaseUrl();

console.log('[Token Manager] Initialized with base URL:', API_BASE_URL);

class TokenManager {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.refreshCheckInterval = null;
  }

  init() {
    this.accessToken = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refresh_token');
    const expiresAt = localStorage.getItem('token_expires_at');
    
    if (expiresAt) {
      this.tokenExpiresAt = parseInt(expiresAt, 10);
    }

    if (this.accessToken && this.refreshToken) {
      this.startTokenRefreshTimer();
    }
  }

  setTokens(accessToken, refreshToken, expiresIn) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    
    const expiresAt = Date.now() + (expiresIn * 1000);
    this.tokenExpiresAt = expiresAt;

    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('token_expires_at', expiresAt.toString());

    this.startTokenRefreshTimer();
  }

  getAccessToken() {
    return this.accessToken;
  }

  getRefreshToken() {
    return this.refreshToken;
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;

    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');

    this.stopTokenRefreshTimer();
  }

  isTokenExpired() {
    if (!this.tokenExpiresAt) {
      return true;
    }
    
    const bufferTime = 60 * 1000;
    return Date.now() >= (this.tokenExpiresAt - bufferTime);
  }

  shouldRefreshToken() {
    if (!this.accessToken || !this.refreshToken) {
      return false;
    }

    const refreshThreshold = 2 * 60 * 1000;
    return this.tokenExpiresAt && (this.tokenExpiresAt - Date.now()) < refreshThreshold;
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push({ resolve, reject });
      });
    }

    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    this.isRefreshing = true;

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refresh_token: this.refreshToken,
      });

      const { access_token, refresh_token, expires_in } = response.data;

      this.setTokens(access_token, refresh_token, expires_in);

      this.refreshSubscribers.forEach(subscriber => {
        subscriber.resolve(access_token);
      });
      this.refreshSubscribers = [];

      return access_token;
    } catch (error) {
      this.refreshSubscribers.forEach(subscriber => {
        subscriber.reject(error);
      });
      this.refreshSubscribers = [];

      this.clearTokens();
      
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  startTokenRefreshTimer() {
    this.stopTokenRefreshTimer();

    this.refreshCheckInterval = setInterval(() => {
      if (this.shouldRefreshToken()) {
        this.refreshAccessToken().catch(err => {
          console.error('Background token refresh failed:', err);
        });
      }
    }, 30 * 1000);
  }

  stopTokenRefreshTimer() {
    if (this.refreshCheckInterval) {
      clearInterval(this.refreshCheckInterval);
      this.refreshCheckInterval = null;
    }
  }

  async ensureValidToken() {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    if (this.isTokenExpired() || this.shouldRefreshToken()) {
      return await this.refreshAccessToken();
    }

    return this.accessToken;
  }
}

const tokenManager = new TokenManager();
tokenManager.init();

export default tokenManager;
